import React, { useState, useEffect, useRef } from "react";
import Menubar from "../Menubar/Menubar";
import { Outlet } from "react-router-dom";
import "./Dashboard.css";
import MyCharts from "./MyCharts";
import { GiNetworkBars } from "react-icons/gi";
import { BsFillLuggageFill } from "react-icons/bs";
import { FaMoneyBillTrendUp } from "react-icons/fa6";
import { IoIosCheckmarkCircle } from "react-icons/io";

const Dashboard = () => {
  // Move ALL hooks to the top, before any conditional returns
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    const savedState = localStorage.getItem("menubarOpen");
    if (savedState !== null) {
      return JSON.parse(savedState);
    }
    return window.innerWidth >= 1024;
  });
  const [bookings, setBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(true);
  const [topProperties, setTopProperties] = useState([]);
  const [analyticsdata, setanalyticsdata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPopup_filter, setShowPopup_filter] = useState(false);
  const [bookingsByDestination, setBookingsByDestination] = useState([]);
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [activeFilter, setActiveFilter] = useState(null);
  const filterRef = useRef(null);

  // Fetch bookings
  useEffect(() => {
    async function fetchBookings() {
      try {
        setLoadingBookings(true);
        const params = new URLSearchParams();
        if (dateRange.startDate)
          params.append("startDate", dateRange.startDate);
        if (dateRange.endDate) params.append("endDate", dateRange.endDate);

        const url = `https://demo.zorbastays.com/web-backend/getAllBookingsDash${
          params.toString() ? `?${params}` : ""
        }`;

        const response = await fetch(url);
        const data = await response.json();
        setBookings(
          data.sort(
            (a, b) => new Date(b.bookingtime) - new Date(a.bookingtime),
          ),
        );
      } catch (error) {
        console.error("Error fetching bookings:", error);
      } finally {
        setLoadingBookings(false);
      }
    }
    fetchBookings();
  }, [dateRange]);

  // Fetch analytics
  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams();
        if (dateRange.startDate)
          params.append("startDate", dateRange.startDate);
        if (dateRange.endDate) params.append("endDate", dateRange.endDate);

        const url = `https://demo.zorbastays.com/web-backend/getAnalytics${
          params.toString() ? `?${params}` : ""
        }`;

        const res = await fetch(url);
        const data = await res.json();

        setanalyticsdata(data);
        setTopProperties(data.topproperties || []);
        setBookingsByDestination(data.bookingsbydestination || []);
      } catch (error) {
        console.error("Error fetching analytics:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [dateRange]);

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (mobile) {
        setIsSidebarOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Handle click outside filter
  useEffect(() => {
    function handleClickOutside(event) {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setShowPopup_filter(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center w-full h-screen bg-gray-50">
        <div className="w-12 h-12 border-4 border-green-500 border-dashed rounded-full animate-spin mb-6"></div>
        <p className="mt-4 text-gray-600 font-medium">Fetching analytics...</p>
      </div>
    );
  }

  if (!analyticsdata) {
    return <p>Failed to load analytics.</p>;
  }

  const recentBookings = activeFilter ? bookings : bookings.slice(0, 5);
  const statusColors = {
    CANCELLED: "bg-amber-800/20 text-amber-800",
    CONFIRMED: "bg-green-500/20 text-green-500",
    BOOKED: "bg-purple-500/20 text-purple-500",
    FAILED: "bg-red-500/20 text-red-500",
    PENDING: "bg-yellow-200/40 text-yellow-300",
  };

  const getDateRange = (label) => {
    const today = new Date();
    const fmt = (d) => d.toISOString().split("T")[0];

    switch (label) {
      case "Today":
        return { startDate: fmt(today), endDate: fmt(today) };
      case "Week": {
        const start = new Date(today);
        start.setDate(today.getDate() - 7);
        return { startDate: fmt(start), endDate: fmt(today) };
      }
      case "Month": {
        const start = new Date(today);
        start.setDate(today.getDate() - 30);
        return { startDate: fmt(start), endDate: fmt(today) };
      }
      case "Year": {
        return {
          startDate: `${today.getFullYear()}-01-01`,
          endDate: `${today.getFullYear()}-12-31`,
        };
      }
      default:
        return { startDate: null, endDate: null };
    }
  };

  return (
    <div className="flex bg-[#F8F9FA]">
      <Menubar
        isOpen={isSidebarOpen}
        setIsOpen={setIsSidebarOpen}
        isMobile={isMobile}
      />

      <main
        className={`p-5 lg:p-6 transition-all duration-500 w-full ${
          isMobile ? "ml-0" : isSidebarOpen ? "ml-64" : "ml-16"
        }`}
      >
        <Outlet />

        {/* Header */}
        <div className="flex justify-between gap-5 items-start lg:items-center">
          <div className="flex flex-col lg:flex-row gap-5 items-center">
            <p className="font-semibold text-xl text-gray-700 ml-10 lg:ml-0">
              Dashboard
            </p>
          </div>

          <div className="flex items-center">
            <div className="flex items-center">
              <div className="relative inline-block" ref={filterRef}>
                <div
                  onClick={() => setShowPopup_filter(true)}
                  className={`flex flex-col items-center gap-4 cursor-pointer select-none group scale-[0.75] py-2.5 px-2 rounded-full border-2 transition-all duration-300 ${
                    showPopup_filter
                      ? "border-[#1D2826]"
                      : "border-transparent hover:border-[#1D2826]"
                  }`}
                >
                  <div className="relative w-6 h-0.5 bg-[#1D2826] rounded-full">
                    <div
                      className="absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full border-2 border-black bg-white transition-all duration-300"
                      style={{
                        left: showPopup_filter ? "100%" : "0%",
                        transform: `translate(${
                          showPopup_filter ? "-100%" : "0"
                        }, -50%)`,
                      }}
                    ></div>
                  </div>

                  <div className="relative w-6 h-0.5 bg-[#1D2826] rounded-full -my-2.5">
                    <div
                      className="absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full border-2 border-black bg-white transition-all duration-300"
                      style={{
                        left: showPopup_filter ? "0%" : "100%",
                        transform: `translate(${
                          showPopup_filter ? "0" : "-100%"
                        }, -50%)`,
                      }}
                    ></div>
                  </div>

                  <div className="relative w-6 h-0.5 bg-[#1D2826] rounded-full">
                    <div
                      className="absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full border-2 border-black bg-white transition-all duration-300"
                      style={{
                        left: showPopup_filter ? "100%" : "0%",
                        transform: `translate(${
                          showPopup_filter ? "-100%" : "0"
                        }, -50%)`,
                      }}
                    ></div>
                  </div>
                </div>

                {showPopup_filter && (
                  <div className="absolute top-full mt-1 right-0 bg-white border rounded-lg shadow-custom z-10 p-4 space-y-4">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Filter
                      </span>
                      <div className="flex items-center gap-4">
                        {(activeFilter ||
                          dateRange.startDate ||
                          dateRange.endDate) && (
                          <button
                            onClick={() => {
                              setActiveFilter(null);
                              setDateRange({ startDate: null, endDate: null });
                            }}
                            className="text-xs text-red-400 hover:text-red-600 font-medium transition-colors"
                          >
                            Reset
                          </button>
                        )}
                        <button
                          onClick={() => setShowPopup_filter(false)}
                          className="text-gray-400 hover:text-gray-700 text-lg leading-none font-medium"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                    <div className="flex justify-between gap-2">
                      {["Today", "Week", "Month", "Year"].map((label) => (
                        <button
                          key={label}
                          onClick={() => {
                            setActiveFilter(label);
                            setDateRange(getDateRange(label));
                            setShowPopup_filter(false);
                          }}
                          className={`flex-1 py-1 px-2 rounded text-sm font-medium transition-colors ${
                            activeFilter === label
                              ? "bg-[#DAE0CE] font-bold"
                              : "bg-gray-100 hover:bg-[#DAE0CE]"
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>

                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-2">
                        <input
                          type="date"
                          value={dateRange.startDate || ""}
                          onChange={(e) => {
                            setActiveFilter(null);
                            setDateRange((prev) => ({
                              ...prev,
                              startDate: e.target.value,
                            }));
                          }}
                          className="border text-sm rounded px-2 py-1 w-full focus:outline-none focus:ring-1 focus:ring-green-600 focus:border-black"
                        />
                        <span className="text-sm text-gray-500">-</span>
                        <input
                          type="date"
                          value={dateRange.endDate || ""}
                          onChange={(e) => {
                            setActiveFilter(null);
                            setDateRange((prev) => ({
                              ...prev,
                              endDate: e.target.value,
                            }));
                          }}
                          className="border text-sm rounded px-2 py-1 w-full focus:outline-none focus:ring-1 focus:ring-green-600 focus:border-black"
                        />
                      </div>
                      {!activeFilter &&
                        dateRange.startDate &&
                        dateRange.endDate && (
                          <button
                            onClick={() => setShowPopup_filter(false)}
                            className="w-full py-1 bg-[#1D2826] text-white text-sm rounded hover:opacity-90"
                          >
                            Apply
                          </button>
                        )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[73%_25%] gap-[2%] items-start mt-5">
          {/* Main col-1 */}
          <div>
            {/* Boxes */}
            <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-3 items-center gap-5 text-gray-700 font-semibold">
              {/* Total Bookings */}
              <div className="flex items-center justify-between gap-5 bg-[#DAE0CE] py-2 px-4 rounded-lg">
                <div>
                  <p>Total Bookings</p>
                  <p className="mt-2 text-lg text-black">
                    {(analyticsdata.totalbookings || 0).toLocaleString()}
                  </p>
                </div>
                <div>
                  <GiNetworkBars className="bg-[#3C4C4E] text-3xl text-white p-1.5 rounded-md" />
                </div>
              </div>

              {/* Upcoming Trips */}
              <div className="flex items-center justify-between gap-5 bg-[#DAE0CE] py-2 px-4 rounded-lg">
                <div>
                  <p>Upcoming Trips</p>
                  <p className="mt-2 text-lg text-black">
                    {(analyticsdata.upcomingtrips || 0).toLocaleString()}
                  </p>
                </div>
                <div>
                  <BsFillLuggageFill className="bg-[#3C4C4E] text-3xl text-white p-1.5 rounded-md" />
                </div>
              </div>

              {/* Total Revenue */}
              <div className="flex items-center justify-between gap-5 bg-[#DAE0CE] py-2 px-4 rounded-lg">
                <div>
                  <p>Total Revenue</p>
                  <p className="mt-2 text-lg text-black">
                    ₹{(parseFloat(analyticsdata.totalrevenue) || 0).toLocaleString()}
                  </p>
                </div>
                <div>
                  <FaMoneyBillTrendUp className="bg-[#3C4C4E] text-3xl text-white p-1.5 rounded-md" />
                </div>
              </div>
            </div>

            {/* Charts */}
            <MyCharts dateRange={dateRange} />

            {/* Table */}
            <div>
              <div className="shadow-md rounded-lg mt-5 overflow-hidden">
                <div className="flex justify-between bg-white rounded-t-lg p-4 text-gray-700">
                  <div>
                    <p className="font-semibold">Recent Bookings</p>
                    <p className="flex items-center text-xs text-gray-500 mt-2">
                      <IoIosCheckmarkCircle className="mr-1 text-lg fill-green-500" />
                      <span className="text-gray-700 font-semibold">
                        {activeFilter
                          ? bookings.length
                          : bookings.filter((b) => {
                              if (!b.bookingtime) return false;
                              const d = new Date(b.bookingtime);
                              const now = new Date();
                              return (
                                d.getMonth() === now.getMonth() &&
                                d.getFullYear() === now.getFullYear()
                              );
                            }).length}{" "}
                        done&nbsp;
                      </span>
                      {activeFilter === "Today"
                        ? "today"
                        : activeFilter === "Week"
                          ? "this week"
                          : activeFilter === "Year"
                            ? "this year"
                            : "this month"}
                    </p>
                  </div>

                  <div>
                    <a
                      href="/Bookings"
                      className="text-gray-400 hover:text-gray-700 text-sm underline underline-offset-2"
                    >
                      View more
                    </a>
                  </div>
                </div>

                <div
                  className={`overflow-x-auto ${activeFilter ? "max-h-96 overflow-y-auto" : ""}`}
                >
                  <table className="w-full text-sm text-left rtl:text-right text-gray-500">
                    <thead className="text-xs text-gray-400 uppercase bg-green-50 border-b">
                      <tr>
                        <th className="px-4 py-3 w-1/10">Customer Name</th>
                        <th className="px-4 py-3 w-1/10">
                          Package / Destination
                        </th>
                        <th className="px-4 py-3 w-1/10">Booking Date</th>
                        <th className="px-4 py-3 w-1/12">Status</th>
                        <th className="px-4 py-3 w-1/10">Payment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loadingBookings ? (
                        <tr>
                          <td
                            colSpan="5"
                            className="text-center py-4 text-gray-500"
                          >
                            Loading...
                          </td>
                        </tr>
                      ) : recentBookings.length > 0 ? (
                        recentBookings.map((b) => (
                          <tr
                            key={b.id || b.bookingid}
                            className="bg-white even:bg-gray-50 border-b border-gray-200 hover:bg-gray-100 text-gray-800"
                          >
                            <td className="px-4 py-2">
                              {b.firstname && b.lastname
                                ? `${b.firstname} ${b.lastname}`
                                : "-"}
                            </td>

                            <td className="px-4 py-2">
                              {b.package
                                ? `${b.package} - ${b.destination}`
                                : b.destination || "-"}
                            </td>

                            <td className="px-4 py-2">
                              {b.bookingtime
                                ? new Date(b.bookingtime).toLocaleDateString(
                                    "en-GB",
                                  )
                                : "-"}
                            </td>

                            <td className="px-4 py-2">
                              <button
                                className={`w-20 px-1 py-1 rounded-lg text-xs scale-90 ${
                                  statusColors[
                                    b.bookingstatus?.toUpperCase()
                                  ] || ""
                                }`}
                              >
                                {b.bookingstatus || "PENDING"}
                              </button>
                            </td>

                            <td className="px-4 py-2">{b.btocprice || "-"}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan="5"
                            className="text-center py-4 text-gray-500"
                          >
                            No bookings found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Main col-2 */}
          <div className="hidden lg:block">
            <div className="bg-white shadow-md p-5 rounded-lg mx-auto">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">
                Bookings
              </h2>

              {bookingsByDestination && bookingsByDestination.length > 0 ? (
                (() => {
                  const totalBookings = bookingsByDestination.reduce(
                    (sum, item) => sum + (item.totalbookings || 0),
                    0,
                  );

                  const colors = [
                    "bg-sky-300",
                    "bg-yellow-200",
                    "bg-orange-300",
                    "bg-teal-300",
                    "bg-red-300",
                    "bg-blue-300",
                  ];

                  return bookingsByDestination.map((item, index) => {
                    const percent = totalBookings > 0
                      ? ((item.totalbookings / totalBookings) * 100).toFixed(0)
                      : 0;
                    return (
                      <div key={index} className="mb-4">
                        <div className="flex justify-between text-sm font-medium text-gray-700 mb-1">
                          <span>{item.destination}</span>
                          <span>{percent}%</span>
                        </div>
                        <div className="w-full h-2 bg-gray-200 rounded">
                          <div
                            className={`h-2 ${
                              colors[index % colors.length]
                            } rounded`}
                            style={{ width: `${percent}%` }}
                          ></div>
                        </div>
                      </div>
                    );
                  });
                })()
              ) : (
                <p className="text-gray-500 text-sm">No data available</p>
              )}

              <h2 className="text-lg font-semibold text-gray-800 mt-8 mb-4">
                Top-Performing Properties
              </h2>

              <div className="grid grid-cols-2 gap-4">
                {topProperties && topProperties.length > 0 ? (
                  topProperties.map((property, index) => (
                    <div
                      key={index}
                      className={`border-2 rounded-xl p-4 flex flex-col items-start ${
                        [
                          "border-sky-500",
                          "border-yellow-300",
                          "border-orange-400",
                          "border-green-400",
                        ][index % 4]
                      }`}
                    >
                      <p className="text-lg font-bold text-gray-800">
                        {property.property}
                      </p>
                      <p className="text-sm mt-1 text-gray-600">
                        {property.totalbookings || 0}
                        <span className="text-sm font-normal ml-1">
                          bookings
                        </span>
                      </p>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-sm col-span-2">
                    No data available
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;